/**
 * Typed fetch wrapper for the same-origin API (BUILD-SPEC §1/§5-B, PHASE1 §7.4).
 *
 * Every request is same-origin (`base URL === ''`) and sends the session cookie
 * via `credentials: 'include'`. Responses follow the envelope `{ data, meta? }`
 * on success and `{ error: { code, message } }` on failure. A `401` means the
 * session is gone: redirect to the OIDC login and abort.
 *
 * Phase-1 transport additions: every non-GET request carries the
 * `X-Requested-With: fetch` CSRF header; `FormData` bodies are sent as-is
 * without a JSON `Content-Type`; `patch`/`put`/`del`/`postForm` verbs plus the
 * raw helpers `requestRaw`/`downloadFile` for CSV export and file upload flows.
 *
 * Transport failures (fetch rejects — offline/server unreachable) are mapped to
 * a German `ApiRequestError` with `code: 'network_error'` so every mutation
 * surface shows a readable toast instead of "Failed to fetch". `AuthGate` does
 * its own fetch for `/api/auth/me` and is unaffected.
 */

/** Optional metadata returned alongside list / paged / inbox responses. */
export interface ApiMeta {
  page?: number;
  limit?: number;
  offset?: number;
  total?: number;
  sum_amount?: string;
  remaining?: number;
  next_cursor?: string | null;
  total_balance?: string;
  /** GET /api/accounts — hidden accounts are excluded from `data`, so their
   * count travels in the meta („Inaktive Konten" section sizing). */
  hidden_count?: number;
}

/** Standard API success envelope. */
export interface ApiResponse<T, M = ApiMeta> {
  data: T;
  meta?: M;
}

/** Standard API error envelope. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
  };
}

/** Typed error thrown by API requests. `code` drives UI branching. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

/** Same-origin base URL — the SPA and the API share one origin in every env. */
const BASE_URL = '';

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

/**
 * Build the header set for a request: JSON `Content-Type` unless the body is
 * `FormData`, and the CSRF header on every state-changing (non-GET) verb.
 */
function buildHeaders(method: string, body: unknown, extra?: HeadersInit): Headers {
  const headers = new Headers(extra);
  if (!isFormData(body) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (method.toUpperCase() !== 'GET' && !headers.has('X-Requested-With')) {
    headers.set('X-Requested-With', 'fetch');
  }
  return headers;
}

/**
 * Low-level request: performs the fetch, handles the 401 → login redirect, and
 * returns the raw `Response`. Callers that need the envelope use `request`.
 */
export async function requestRaw(path: string, options?: RequestInit): Promise<Response> {
  const method = options?.method ?? 'GET';
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...options,
      method,
      credentials: 'include',
      headers: buildHeaders(method, options?.body, options?.headers),
    });
  } catch (err) {
    // Deliberate aborts keep their identity; everything else a fetch rejection
    // can be is a transport failure (TypeError): offline or server unreachable.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiRequestError(
      method.toUpperCase() === 'GET'
        ? 'Keine Verbindung zum Server.'
        : 'Keine Verbindung — Änderung nicht möglich',
      0,
      'network_error',
    );
  }

  if (response.status === 401) {
    // Session expired or missing — hand off to the OIDC login flow.
    window.location.href = '/api/auth/login';
    throw new ApiRequestError('Nicht angemeldet', 401, 'unauthenticated');
  }

  return response;
}

async function request<T, M = ApiMeta>(
  path: string,
  options?: RequestInit,
): Promise<ApiResponse<T, M>> {
  const response = await requestRaw(path, options);

  // 204 No Content — nothing to parse (DELETE / logout).
  if (response.status === 204) {
    return { data: undefined as T };
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw new ApiRequestError(
      `Unerwartete Antwort vom Server (HTTP ${response.status})`,
      response.status,
      'invalid_response',
    );
  }

  if (!response.ok) {
    const body = json as Partial<ApiErrorBody>;
    throw new ApiRequestError(
      body.error?.message ?? `Anfrage fehlgeschlagen (HTTP ${response.status})`,
      response.status,
      body.error?.code ?? 'error',
    );
  }

  return json as ApiResponse<T, M>;
}

function jsonBody(body: unknown): BodyInit | undefined {
  if (body === undefined) return undefined;
  if (isFormData(body)) return body;
  return JSON.stringify(body);
}

export const api = {
  async get<T>(path: string): Promise<T> {
    const envelope = await request<T>(path, { method: 'GET' });
    return envelope.data;
  },

  async getEnvelope<T, M = ApiMeta>(path: string): Promise<ApiResponse<T, M>> {
    return request<T, M>(path, { method: 'GET' });
  },

  async post<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T> {
    const envelope = await request<T>(path, {
      method: 'POST',
      body: jsonBody(body),
      headers,
    });
    return envelope.data;
  },

  async postEnvelope<T, M = ApiMeta>(
    path: string,
    body?: unknown,
    headers?: HeadersInit,
  ): Promise<ApiResponse<T, M>> {
    return request<T, M>(path, { method: 'POST', body: jsonBody(body), headers });
  },

  /** POST a multipart `FormData` payload (file upload). */
  async postForm<T>(path: string, form: FormData): Promise<T> {
    const envelope = await request<T>(path, { method: 'POST', body: form });
    return envelope.data;
  },

  async patch<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T> {
    const envelope = await request<T>(path, {
      method: 'PATCH',
      body: jsonBody(body),
      headers,
    });
    return envelope.data;
  },

  async put<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<T> {
    const envelope = await request<T>(path, {
      method: 'PUT',
      body: jsonBody(body),
      headers,
    });
    return envelope.data;
  },

  /** DELETE — expects a `204 No Content`. */
  async del(path: string): Promise<void> {
    await request<unknown>(path, { method: 'DELETE' });
  },

  /**
   * Download a file to disk. Reads the server filename from the
   * `Content-Disposition` header when present, else uses `fallbackName`.
   */
  async downloadFile(path: string, fallbackName: string): Promise<void> {
    const response = await requestRaw(path, { method: 'GET' });
    if (!response.ok) {
      throw new ApiRequestError(
        `Download fehlgeschlagen (HTTP ${response.status})`,
        response.status,
        'download_failed',
      );
    }
    const blob = await response.blob();
    const filename = filenameFromDisposition(
      response.headers.get('Content-Disposition'),
      fallbackName,
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
};

/** Extract a filename from a `Content-Disposition` header, else the fallback. */
function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return fallback;
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? fallback;
}
